import React, { useState, useCallback } from 'react';
import Toast from './Toast';
import { ToastWrapper } from './styles';

interface ToastMessage {
  id: string;
  title: string;
  body: string;
  conversationId?: number;
}

interface ToastManagerProps {
  onToastClick?: (conversationId: number) => void;
  currentChatId?: number | null;
}

const ToastManager: React.FC<ToastManagerProps> = ({ onToastClick, currentChatId }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((toast: ToastMessage) => {
    // Não mostrar toast se o chat relacionado estiver aberto E a janela estiver em foco
    if (toast.conversationId && currentChatId && toast.conversationId === currentChatId) {
      // Se a janela está em foco, não mostrar toast (atualizar contador silenciosamente)
      if (document.hasFocus() && document.visibilityState === 'visible') {
        console.log('ToastManager: Ignorando toast - chat está aberto e janela em foco:', toast.conversationId);
        // Disparar evento para atualizar contador de notificações silenciosamente
        window.dispatchEvent(new CustomEvent('fcmNotificationReceived', { 
          detail: { data: { conversation_id: toast.conversationId } } 
        }));
        return;
      }
      // Se não está em foco, mostrar toast mesmo assim
    }
    
    setToasts((prev) => [...prev, toast]);
  }, [currentChatId]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const handleToastClick = useCallback(
    (conversationId: number) => {
      if (onToastClick) {
        onToastClick(conversationId);
      }
    },
    [onToastClick]
  );

  // Expor método para adicionar toast via window
  React.useEffect(() => {
    const showToast = (toast: ToastMessage) => {
      console.log('ToastManager: Recebendo toast:', toast);
      addToast(toast);
    };
    
    (window as any).showToast = showToast;
    
    console.log('ToastManager: Método showToast exposto no window');
    
    return () => {
      delete (window as any).showToast;
    };
  }, [addToast]);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <ToastWrapper>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          id={toast.id}
          title={toast.title}
          body={toast.body}
          conversationId={toast.conversationId}
          onClose={removeToast}
          onClick={handleToastClick}
        />
      ))}
    </ToastWrapper>
  );
};

export default ToastManager;
